import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSmartBack } from '../hooks/useSmartBack';
import {
  ArrowLeft, Info, ChevronRight,
  User, Shield, FileText, Crown, Bug, LogOut, Sun, Moon, ShieldAlert,
  Mail, Send, BarChart2, PlayCircle, GraduationCap,
} from 'lucide-react';
import { useTour } from '../tour/TourContext';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import Logo from '../components/Logo';
import ProfileDetails from './profile/ProfileDetails';
import PlaceholderSection from './profile/PlaceholderSection';
import BacktestSection from './profile/BacktestSection';
import RiskProfileSection from './profile/RiskProfileSection';
import ReportsSection from './profile/ReportsSection';
import ReportIssue from './profile/ReportIssue';
import SubscriptionSection from './profile/SubscriptionSection';
import InviteSection from './profile/InviteSection';
import ContactSection from './profile/ContactSection';

const SETTINGS = [
  { label: 'Profile Details',      icon: User,        section: 'profile-details' },
  { label: 'Trading Risk Profile', icon: Shield,      section: 'risk-profile'    },
  { label: 'Trade Reports',        icon: FileText,    section: 'reports'         },
  { label: 'Mentorship',           icon: GraduationCap, path: '/mentorship'      },
  { label: 'Trade Analysis',       icon: BarChart2,   path: '/analytics'         },
  { label: 'Subscription',         icon: Crown,       section: 'subscription'    },
  { label: 'Contact Us',           icon: Mail,        section: 'contact'         },
  { label: 'About',                icon: Info,        section: 'about'           },
];

const ADMIN_ONLY_SETTINGS = [
  { label: 'Send Invite',          icon: Send,     section: 'invite',        adminOnly: true },
  { label: 'Report Issue',         icon: Bug,      section: 'report-issue',  adminOnly: true },
];

function ProfilePage({ initialSection = null }) {
  const { colors, theme, toggleTheme } = useTheme();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const smartBack = useSmartBack('/profile');
  const [showLogout, setShowLogout] = useState(false);
  const { openLauncher } = useTour();

  // Read section from URL query param — survives refresh
  const searchParams = new URLSearchParams(location.search);
  const urlSection = searchParams.get('section');
  const activeSection = urlSection || location.state?.section || initialSection || null;
  const isLocked = location.state?.locked === true || searchParams.get('locked') === '1';

  const onBack = isLocked ? null : smartBack;

  const handleSetting = (item) => {
    if (item.action === 'openTour') { openLauncher(); return; }
    if (item.path) { navigate(item.path); return; }
    navigate(`/profile?section=${item.section}`);
  };

  const renderContent = () => {
    switch (activeSection) {
      case 'profile-details': return <ProfileDetails onBack={onBack} />;
      case 'backtest': return <BacktestSection onBack={onBack} />;
      case 'risk-profile': return <RiskProfileSection onBack={onBack} locked={isLocked} />;
      case 'reports': return <ReportsSection onBack={onBack} />;
      case 'topup': return <PlaceholderSection onBack={onBack} title="Virtual Money Top-up" description="Reset or add to your balance. Coming soon!" />;
      case 'subscription': return <SubscriptionSection onBack={onBack} />;
      case 'features': return <PlaceholderSection onBack={onBack} title="Features" description="Explore all features. Coming soon!" />;
      case 'about':
        return (
          <div className="space-y-4">
            <div className="flex items-center space-x-3">
              <button onClick={onBack} className="app-card p-1.5 rounded-md" style={{ backgroundColor: colors.cardBg }}>
                <ArrowLeft className="w-4 h-4" style={{ color: colors.primaryText }} />
              </button>
              <h1 className="text-[17px] font-bold" style={{ color: colors.primaryText }}>About Us</h1>
            </div>
            <div className="app-card rounded-lg p-1.5 text-center" style={{ backgroundColor: colors.cardBg, borderWidth: '1px', borderColor: colors.border }}>
              <Logo size="md" className="justify-center mb-3" />
              <h2 className="text-[14px] font-bold mb-1" style={{ color: colors.primaryText }}>TradeReflex</h2>
              <p className="text-[13px] mb-2" style={{ color: colors.secondaryText }}>
                AI-powered paper trading platform. Practice with virtual money, analyze with AI.
              </p>
              <p className="text-[13px]" style={{ color: colors.secondaryText }}>Version 2.0.0</p>
            </div>
          </div>
        );
      case 'report-issue': return <ReportIssue onBack={onBack} />;
      case 'invite':       return <InviteSection onBack={onBack} />;
      case 'contact':      return <ContactSection onBack={onBack} />;
      default: return null;
    }
  };

  // Section view
  if (activeSection) {
    return <div data-testid="profile-section">{renderContent()}</div>;
  }

  // Default landing — user card + settings list
  const planBadge = {
    admin:   'bg-red-500/20 text-red-400',
    premium: 'bg-indigo-500/20 text-indigo-400',
  }[user?.plan] || 'bg-gray-500/20 text-gray-400';

  return (
    <div className="space-y-4 pb-4">

      {/* User card */}
      <div
        className="flex items-center justify-between px-3 py-3 rounded-2xl cursor-pointer"
        style={{ background: `linear-gradient(135deg, ${colors.accent}14 0%, ${colors.accent}04 100%)`, border: `1px solid ${colors.accent}30` }}
        onClick={() => navigate('/profile?section=profile-details')}
      >
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: `${colors.accent}25`, border: `2px solid ${colors.accent}40` }}>
            <User className="w-5 h-5" style={{ color: colors.accent }} />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <p className="text-[15px] font-bold" style={{ color: colors.primaryText }}>
                {user?.full_name || 'Demo Trader'}
              </p>
              {user?.plan && (
                <span className={`px-1.5 py-0.5 rounded text-[11px] font-semibold uppercase tracking-wide ${planBadge}`}>
                  {user.plan}
                </span>
              )}
            </div>
            <p className="text-[12px]" style={{ color: colors.secondaryText }}>{user?.email}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <ChevronRight className="w-4 h-4" style={{ color: colors.secondaryText }} />
        </div>
      </div>

      {/* Settings list */}
      <div>
        <p className="text-[11px] font-semibold uppercase tracking-wider mb-2 px-0.5"
          style={{ color: colors.secondaryText }}>Settings</p>
        <div className="rounded-2xl overflow-hidden"
          style={{ background: `linear-gradient(135deg, ${colors.accent}10 0%, ${colors.accent}03 100%)`, border: `1px solid ${colors.accent}28` }}>
          {(user?.plan === 'admin'
            ? [{ label: 'Admin Panel', icon: ShieldAlert, path: '/admin', adminOnly: true }, ...ADMIN_ONLY_SETTINGS, ...SETTINGS]
            : SETTINGS
          ).map((item, idx, arr) => {
            const Icon = item.icon;
            const isAdmin = item.adminOnly;
            return (
              <button
                key={item.label}
                onClick={() => handleSetting(item)}
                className="w-full flex items-center justify-between px-3 py-2.5 transition-colors"
                style={{ borderBottom: idx < arr.length - 1 ? `1px solid ${colors.border}` : 'none' }}
                onMouseEnter={e => e.currentTarget.style.backgroundColor = isAdmin ? 'rgba(239,68,68,0.08)' : `${colors.accent}08`}
                onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                <div className="flex items-center gap-2.5">
                  <Icon className="w-3.5 h-3.5 flex-shrink-0" style={{ color: isAdmin ? '#ef4444' : colors.secondaryText }} />
                  <span className="text-[14px] font-medium" style={{ color: isAdmin ? '#ef4444' : colors.primaryText }}>{item.label}</span>
                </div>
                <ChevronRight className="w-3.5 h-3.5" style={{ color: colors.secondaryText }} />
              </button>
            );
          })}

          {/* Logout */}
          <button
            onClick={() => setShowLogout(true)}
            className="w-full flex items-center gap-2.5 px-3 py-2.5 transition-colors"
            onMouseEnter={e => e.currentTarget.style.backgroundColor = `${colors.loss}08`}
            onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
          >
            <LogOut className="w-3.5 h-3.5 flex-shrink-0" style={{ color: colors.loss }} />
            <span className="text-[14px] font-medium" style={{ color: colors.loss }}>Logout</span>
          </button>
        </div>
      </div>

      {/* Logout confirm */}
      {showLogout && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4"
          style={{ backgroundColor: colors.overlayBg }}
          onClick={() => setShowLogout(false)}>
          <div className="w-full max-w-sm rounded-xl p-4 space-y-4"
            style={{ background: `linear-gradient(135deg, ${colors.accent}40 0%, ${colors.accent}15 100%), ${colors.cardBg}`, border: `1px solid ${colors.accentBorder}` }}
            onClick={e => e.stopPropagation()}>
            <div className="text-center space-y-1">
              <p className="text-[15px] font-semibold" style={{ color: colors.primaryText }}>Log out?</p>
              <p className="text-[13px]" style={{ color: colors.secondaryText }}>You'll need to sign in again to access your account.</p>
            </div>
            <div className="flex gap-3">
              <button onClick={() => setShowLogout(false)}
                className="flex-1 py-2.5 rounded-xl text-[14px] font-medium"
                style={{ backgroundColor: colors.background, color: colors.primaryText, border: `1px solid ${colors.border}` }}>
                Cancel
              </button>
              <button onClick={() => { logout(); navigate('/login'); }}
                className="flex-1 py-2.5 rounded-xl text-[14px] font-semibold"
                style={{ backgroundColor: colors.loss, color: '#fff' }}>
                Log out
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ProfilePage;
